/**
 * Camera frustum: a 3D representation of what a camera can see, given its
 * mounting position, orientation, and lens.
 *
 * Coordinate convention:
 *   World 3D — right-handed:
 *     x_world = east   (matches SVG x)
 *     y_world = north  (= -y_svg, because SVG y increases downward)
 *     z_world = up     (height above ground)
 *
 *   SVG screen — y inverted:
 *     x_svg  = east  = x_world
 *     y_svg  = south = -y_world
 *
 *   Camera orientation:
 *     pan (rotation around vertical, deg): 0° = looking north,
 *                                          increases clockwise (90° = east).
 *     tilt (deg):  0° = horizontal, +tilt = looking down toward ground.
 *
 *   Lens: HFOV / VFOV are the full-angle horizontal and vertical fields of
 *   view at the lens. VFOV is computed from HFOV using sensor aspect via
 *   `lensMath.ts:getVfovHalfRad` — single source of truth.
 *
 * Per EN 62676-4 / IEC 62676-4:2025 — geometric optics for rectilinear
 * lenses. Fisheye / equidistant projections handled in `projection.ts`.
 */

import type { TerrainModel } from '@/lib/terrain';

/** Position + orientation + lens of a single camera. */
export interface CameraFrustum {
  /** SVG/screen x of camera mount point (pixels). Pass through unchanged. */
  x: number;
  /** SVG/screen y of camera mount point (pixels). */
  y: number;
  /** Mounting height above the floor reference plane (metres). */
  h: number;
  /** Pan: clockwise rotation from north (degrees). 0 = up on map. */
  panDeg: number;
  /** Tilt: degrees below horizontal. 0 = horizontal, 30 = looking 30° down. */
  tiltDeg: number;
  /** Roll: degrees about the optical axis, CLOCKWISE AS SEEN FROM BEHIND the
   *  camera — the installer's own view when standing behind the housing and
   *  turning it by hand. Omitted means 0, which is the arrangement every
   *  formula in this codebase assumed before roll existed. */
  rollDeg?: number;
  /** Horizontal field of view (full angle, degrees). */
  hfovDeg: number;
  /** Vertical field of view (full angle, degrees). */
  vfovDeg: number;
  /** How many imagers a panoramic body fans its span across. Read only by the
   *  'fan' lens model; undefined means the model derives it from the span. */
  imagerCount?: number;
  /** Maximum render distance (metres) — used to clamp rays that don't hit ground. */
  renderRangeM: number;
  /** Conversion factor from metres to SVG pixels at the current map zoom. */
  pixelsPerMeter: number;
  /** Geographic latitude of the camera mount (decimal degrees). Optional —
   *  only required for terrain-aware ground projection. */
  lat?: number;
  /** Geographic longitude of the camera mount (decimal degrees). */
  lng?: number;
  /** Terrain model. When supplied together with `lat`/`lng`, ray-ground
   *  intersection iterates against the actual ground elevation instead of
   *  assuming z=0 everywhere. */
  terrain?: TerrainModel;
}

/** A 3D unit-ish vector. */
export interface Vec3 {
  /** East component (= +x_svg direction). */
  east: number;
  /** North component (= -y_svg direction). */
  north: number;
  /** Up component (= +z_world direction). */
  up: number;
}

/** Build a CameraFrustum from raw renderer state.
 *
 * Defensive defaults match `CameraRenderer.tsx`:
 *   mountingHeight ?? 3 m
 *   tiltAngle      ?? 15° down
 */
/**
 * How far along the GROUND a straight-line range reaches from a lens at height
 * `h`.
 *
 * `range` is a distance FROM THE LENS — that is what a datasheet IR figure
 * means, and what the DORI machinery assumes when it converts a threshold
 * distance into a ground radius. The surface of constant range is therefore a
 * sphere about the lens, and a sphere cut by the ground plane is a circle about
 * the NADIR of radius √(R² − h²), not R.
 *
 * The difference is h²/2R to first order: 15 cm on a 3 m mount at 30 m, and
 * 2.7 m on a 10 m pole at 20 m. The cone's far edge used to clamp the plain
 * horizontal distance to R while the DORI bands drawn inside it already used
 * this formula, so one number produced two radii on the same drawing.
 *
 * Zero when the sphere never reaches the ground (R ≤ h): there is no ground
 * within range at all, which the renderers already have a path for.
 */
export function groundReachM(rangeM: number, mountingHeightM: number): number {
  const r2 = rangeM * rangeM - mountingHeightM * mountingHeightM;
  return r2 > 0 ? Math.sqrt(r2) : 0;
}

export function buildFrustum(args: {
  x: number;
  y: number;
  mountingHeight?: number;
  tiltAngle?: number;
  panDeg: number;
  /** Degrees about the optical axis; see CameraFrustum.rollDeg. Omitted = 0. */
  rollDeg?: number;
  hfovDeg: number;
  vfovDeg: number;
  rangeM: number;
  pixelsPerMeter: number;
  lat?: number;
  lng?: number;
  terrain?: TerrainModel;
  /** Imagers this body fans its span across. Only read by the 'fan' lens
   *  model; omitted means "derive it from the span". */
  imagerCount?: number;
}): CameraFrustum {
  return {
    x: args.x,
    y: args.y,
    imagerCount: args.imagerCount,
    h: args.mountingHeight ?? 3,
    panDeg: args.panDeg,
    tiltDeg: args.tiltAngle ?? 15,
    rollDeg: args.rollDeg,
    hfovDeg: args.hfovDeg,
    vfovDeg: args.vfovDeg,
    renderRangeM: args.rangeM,
    pixelsPerMeter: args.pixelsPerMeter,
    lat: args.lat,
    lng: args.lng,
    terrain: args.terrain,
  };
}

/** Camera basis vectors in world frame, after pan + tilt rotations.
 *
 *  forward: where the camera is pointed (length 1 in world).
 *  right:   horizontal, +90° clockwise from forward in the ground plane.
 *  upCam:   camera-local up axis (perpendicular to forward and right).
 */
export interface CameraBasis {
  forward: Vec3;
  right: Vec3;
  upCam: Vec3;
}

/**
 * @param rollDeg Rotation about the optical axis, CLOCKWISE AS SEEN FROM BEHIND
 *   the camera. That is the installer's own view: stand behind the housing, turn
 *   it clockwise, `rollDeg` goes up. Formally it is the right-handed rotation
 *   about `forward`, which is the same motion seen from the far side.
 *
 *   Omitted or 0 returns the unrolled basis, and `right` is then horizontal.
 *   THAT IS A LOAD-BEARING PROPERTY, not an implementation detail: every closed
 *   form in lensMath (`nearBlindZoneM`, `farGroundReachM`) solves the dead zone
 *   from the frame's CENTRE-BOTTOM ray, and the centre-bottom ray is only the
 *   frame's most downward ray while `right.up === 0`. Roll moves that extreme
 *   toward a corner, so those formulas are wrong for a rolled camera and must be
 *   replaced before anything is allowed to set a nonzero roll here.
 *
 *   90° is exactly corridor mount: `right` becomes the downward axis, so the
 *   sensor's wide axis stands vertical and the cone drawn on the map narrows to
 *   the lens's VFOV — the same number `getDisplayedHfovDeg` returns today for
 *   `corridorMode`. The two agree by construction, which is what will let
 *   corridor collapse into roll later instead of double-counting against it.
 */
export function cameraBasis(panDeg: number, tiltDeg: number, rollDeg = 0): CameraBasis {
  const pan = (panDeg * Math.PI) / 180;
  const tilt = (tiltDeg * Math.PI) / 180;
  const sP = Math.sin(pan);
  const cP = Math.cos(pan);
  const sT = Math.sin(tilt);
  const cT = Math.cos(tilt);

  // Forward: pan rotates in horizontal plane, tilt drops the look-direction.
  // pan=0  → north (+y_world)
  // pan=90 → east  (+x_world)
  // tilt+ → look down (negative up component)
  const forward: Vec3 = {
    east: sP * cT,
    north: cP * cT,
    up: -sT,
  };

  // Right: clockwise 90° from forward in horizontal plane (independent of tilt).
  // Always horizontal — "right" of looking direction.
  const right: Vec3 = {
    east: cP,
    north: -sP,
    up: 0,
  };

  // upCam = right × forward (right-handed cross product, gives camera's local up)
  // After tilt down, upCam tips forward (matches a real camera's frame).
  const upCam: Vec3 = {
    east: sP * sT,
    north: cP * sT,
    up: cT,
  };

  // The rotation below would return these very numbers at φ = 0 (cos 0 = 1 and
  // sin 0 = 0 are both exact in IEEE, so each component survives untouched), so
  // this guard buys legibility and a little cost, never correctness. It is here
  // so a reader can see at a glance that every project saved to date, none of
  // which carries a roll, leaves this function exactly as it always has.
  //
  // Also the NaN trap: a corrupt roll falls in here rather than poisoning the
  // basis, and `effectiveRollDeg` catches the same case one level up.
  if (!rollDeg) return { forward, right, upCam };

  // Right-handed rotation of the SENSOR about the optical axis. `forward` is the
  // rotation axis, so it is untouched; only the two axes spanning the image
  // plane turn:
  //     right' = right·cos φ − upCam·sin φ
  //     upCam' = upCam·cos φ + right·sin φ
  const roll = (rollDeg * Math.PI) / 180;
  const cR = Math.cos(roll);
  const sR = Math.sin(roll);
  return {
    forward,
    right: {
      east: right.east * cR - upCam.east * sR,
      north: right.north * cR - upCam.north * sR,
      up: right.up * cR - upCam.up * sR,
    },
    upCam: {
      east: upCam.east * cR + right.east * sR,
      north: upCam.north * cR + right.north * sR,
      up: upCam.up * cR + right.up * sR,
    },
  };
}

/** Direction of one frustum corner ray, in world frame.
 *
 *  sH ∈ {-1, +1}: -1 = left edge of frame, +1 = right edge.
 *  sV ∈ {-1, +1}: -1 = lower edge of frame, +1 = upper edge.
 *
 *  Result is unnormalised — magnitude depends on the |tan| of the half-FOVs,
 *  but direction is correct. Callers either normalise or ignore magnitude
 *  (e.g. the ground-intersection routine uses the up-component sign only).
 */
export function frustumCornerRay(
  basis: CameraBasis,
  hfovHalfRad: number,
  vfovHalfRad: number,
  sH: -1 | 1,
  sV: -1 | 1,
): Vec3 {
  const tanH = Math.tan(hfovHalfRad);
  const tanV = Math.tan(vfovHalfRad);
  // ray = forward + sH*tanH * right + sV*tanV * upCam
  return {
    east: basis.forward.east + sH * tanH * basis.right.east + sV * tanV * basis.upCam.east,
    north: basis.forward.north + sH * tanH * basis.right.north + sV * tanV * basis.upCam.north,
    up: basis.forward.up + sH * tanH * basis.right.up + sV * tanV * basis.upCam.up,
  };
}

/**
 * Widest half-field this frame model will build a ray for.
 *
 * The rectilinear frame is a rectangle on the tan plane, so a half-angle at 90°
 * asks for tan(π/2) and the corner runs to infinity. 89° keeps tan finite and
 * well conditioned (57.3) and costs nothing real: `defaultLensProjection` has
 * long since left `rectilinear` by then, and a lens with 178° of field has no
 * dead zone left to compute — it already contains straight down, which the
 * containment branch below answers exactly.
 */
const MAX_FRAME_HALF_RAD = (89 * Math.PI) / 180;

/** Degrees below horizontal for a direction. Negative = above the horizon. */
function depressionDeg(v: Vec3): number {
  const m = Math.hypot(v.east, v.north, v.up);
  if (m < 1e-12) return 0;
  return (Math.asin(Math.max(-1, Math.min(1, -v.up / m))) * 180) / Math.PI;
}

const vCross = (a: Vec3, b: Vec3): Vec3 => ({
  east: a.north * b.up - a.up * b.north,
  north: a.up * b.east - a.east * b.up,
  up: a.east * b.north - a.north * b.east,
});
const vDot = (a: Vec3, b: Vec3): number => a.east * b.east + a.north * b.north + a.up * b.up;
const vNorm = (a: Vec3): Vec3 => {
  const m = Math.hypot(a.east, a.north, a.up) || 1;
  return { east: a.east / m, north: a.north / m, up: a.up / m };
};

/** How steeply and how shallowly a frame looks, in degrees below horizontal. */
export interface FrameDepressionRange {
  /** The frame's most DOWNWARD ray. 90 = the frame contains straight down. */
  steepestDeg: number;
  /** The frame's most UPWARD ray. Negative = its top edge clears the horizon. */
  shallowestDeg: number;
}

/**
 * The extreme elevations anywhere in a rectilinear frame, exactly.
 *
 * WHY THIS EXISTS. `tilt ± vfov/2` answers the same question in one line and is
 * right for an upright sensor, because the frame's most downward ray is then its
 * CENTRE-BOTTOM: `right` is horizontal, so elevation is constant along the
 * bottom edge and the centre wins on ray length alone. Roll destroys that. The
 * extreme migrates along the edge toward a corner and reaches measurably
 * further down: on a 90° lens at 15° of tilt, 39.86° upright against 49.44° at
 * 30° of roll (`geometry/__tests__/cameraRoll.test.ts`).
 *
 * HOW. The frame maps to a spherical quadrilateral: its four straight edges each
 * span a plane through the lens, so each becomes a GREAT-CIRCLE ARC. The ray
 * closest in angle to nadir is therefore either a corner, or the foot of the
 * perpendicular from nadir onto one of those four arcs — which is nadir's
 * projection into that arc's plane, kept only when it lands between the arc's
 * endpoints. Four planes, four arcs, eight candidates, no sampling and no
 * iteration. The zenith side answers the far edge the same way.
 *
 * The containment branch is not a special case bolted on: if nadir lies INSIDE
 * the quadrilateral the camera sees straight down, the answer is 90°, and no
 * boundary point would have found it.
 *
 * Pan is not a parameter because elevation cannot depend on it — turning about
 * the vertical moves no ray up or down. The old formulas made the same promise
 * in prose; here it is structural.
 */
export function frameDepressionRangeDeg(
  tiltDeg: number,
  hfovHalfRad: number,
  vfovHalfRad: number,
  rollDeg = 0,
): FrameDepressionRange {
  const hHalf = Math.min(Math.max(hfovHalfRad, 0), MAX_FRAME_HALF_RAD);
  const vHalf = Math.min(Math.max(vfovHalfRad, 0), MAX_FRAME_HALF_RAD);
  const basis = cameraBasis(0, tiltDeg, rollDeg);
  const corners = frustumCornerRays(basis, hHalf, vHalf).map(vNorm);

  const NADIR: Vec3 = { east: 0, north: 0, up: -1 };
  const ZENITH: Vec3 = { east: 0, north: 0, up: 1 };

  // Edge planes, consistently wound because `frustumCornerRays` returns the ring
  // in order. A point is inside a convex spherical polygon exactly when it sits
  // on the same side of every one of them.
  const planes = corners.map((c, i) => vCross(c, corners[(i + 1) % corners.length]));
  const inside = (p: Vec3): boolean => {
    // The forward test is not a shortcut, it is the correction for a trap the
    // same-sign rule walks straight into: negating a point negates every dot
    // product, so the ANTIPODE of an interior direction satisfies the rule just
    // as well. Without this line a camera aimed 70° down reported that it could
    // see the zenith, and answered "reaches the horizon" for a frame pointed at
    // its own feet. The frame lies inside the hemisphere about `forward`
    // (half-angles are clamped below 90°), so this settles which of the two
    // regions the rule accepted.
    if (vDot(p, basis.forward) <= 0) return false;
    let sign = 0;
    for (const n of planes) {
      const d = vDot(p, n);
      if (Math.abs(d) < 1e-12) continue; // on the edge: not a witness either way
      const s = d > 0 ? 1 : -1;
      if (sign === 0) sign = s;
      else if (s !== sign) return false;
    }
    return sign !== 0;
  };

  const candidates: Vec3[] = [...corners];
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    const n = planes[i];
    const nMag = Math.hypot(n.east, n.north, n.up);
    if (nMag < 1e-12) continue; // degenerate edge (zero-width frame)
    const nHat = { east: n.east / nMag, north: n.north / nMag, up: n.up / nMag };
    for (const target of [NADIR, ZENITH]) {
      const along = vDot(target, nHat);
      const p: Vec3 = {
        east: target.east - along * nHat.east,
        north: target.north - along * nHat.north,
        up: target.up - along * nHat.up,
      };
      if (Math.hypot(p.east, p.north, p.up) < 1e-9) continue; // target is the pole of this plane
      const foot = vNorm(p);
      // Between the endpoints? Both turns from a to foot and foot to b must run
      // the same way round the plane's normal.
      if (vDot(vCross(a, foot), n) >= 0 && vDot(vCross(foot, b), n) >= 0) candidates.push(foot);
    }
  }

  let steepestDeg = -Infinity;
  let shallowestDeg = Infinity;
  for (const c of candidates) {
    const d = depressionDeg(c);
    if (d > steepestDeg) steepestDeg = d;
    if (d < shallowestDeg) shallowestDeg = d;
  }
  if (inside(NADIR)) steepestDeg = 90;
  if (inside(ZENITH)) shallowestDeg = -90;
  return { steepestDeg, shallowestDeg };
}

/** All four frustum corner rays in canonical order:
 *   [0] lower-left  — closest to ground, left side of frame
 *   [1] lower-right — closest to ground, right side
 *   [2] upper-right — farthest from ground, right side
 *   [3] upper-left  — farthest from ground, left side
 *
 *  Order is counter-clockwise when looking down the optical axis from camera,
 *  matching standard polygon-winding for SVG fill.
 */
export function frustumCornerRays(
  basis: CameraBasis,
  hfovHalfRad: number,
  vfovHalfRad: number,
): [Vec3, Vec3, Vec3, Vec3] {
  return [
    frustumCornerRay(basis, hfovHalfRad, vfovHalfRad, -1, -1),
    frustumCornerRay(basis, hfovHalfRad, vfovHalfRad, +1, -1),
    frustumCornerRay(basis, hfovHalfRad, vfovHalfRad, +1, +1),
    frustumCornerRay(basis, hfovHalfRad, vfovHalfRad, -1, +1),
  ];
}
